import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Typography, Button, Paper, Stack, CircularProgress, Alert,
  Table, TableHead, TableRow, TableCell, TableBody, TextField,
} from '@mui/material';

const API = 'https://portal.peaksoftcon.com:8081/api/bot';

const TR_DAYS = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];

// Best-effort human description for the most common 5-field cron patterns
// the bot actually uses. Anything weird falls back to the raw expression.
function describeCron(expr) {
  const parts = (expr || '').trim().split(/\s+/);
  if (parts.length !== 5) return expr;
  const [m, h, dom, mon, dow] = parts;
  const time = `${h.padStart(2, '0')}:${m.padStart(2, '0')}`;
  if (dom === '*' && mon === '*' && dow === '*') return `Her gün, ${time}`;
  if (dom !== '*' && mon === '*' && dow === '*') return `Her ayın ${dom}'i, ${time}`;
  if (dow !== '*' && dom === '*' && mon === '*' && /^\d$/.test(dow)) {
    return `Her ${TR_DAYS[Number(dow)]}, ${time}`;
  }
  return expr;
}

const JOB_LABELS = {
  'run-all': 'Tüm Firmaları Çek',
  'retention': 'Eski Dosya Temizliği',
};

export default function AdminSystem() {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [cleanResult, setCleanResult] = useState(null);
  const [retentionDays, setRetentionDays] = useState(60);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/schedules`);
      setSchedules(data.schedules || []);
    } catch (err) {
      alert('Schedules yüklenemedi: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const triggerCleanup = async () => {
    const days = Math.max(0, Number(retentionDays) || 0);
    const msg = days === 0
      ? '⚠ TÜM run klasörleri ve DB kayıtları silinecek (in-progress run dahil değil).'
      : `${days} günden eski run klasörleri ve DB kayıtları silinecek.`;
    if (!window.confirm(`${msg}\nBu işlem geri alınamaz.\n\nDevam edilsin mi?`)) return;
    setCleaning(true);
    setCleanResult(null);
    try {
      const { data } = await axios.post(`${API}/cleanup`, { retentionDays: days });
      setCleanResult(data);
    } catch (err) {
      alert('Cleanup hatası: ' + (err.response?.data?.error || err.message));
    } finally {
      setCleaning(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" mb={3}>Sistem</Typography>

      <Typography variant="h6" mb={1}>Zamanlanmış Görevler</Typography>
      <Paper sx={{ p: 2, mb: 4, overflowX: 'auto' }}>
        {loading && <CircularProgress size={20} />}
        {!loading && schedules.length === 0 && (
          <Typography color="text.secondary">Aktif zamanlanmış görev yok (RUN_ALL_CRON ve RETENTION_CRON .env'de kapalı olabilir).</Typography>
        )}
        {!loading && schedules.length > 0 && (
          <Table size="small" sx={{ minWidth: 480 }}>
            <TableHead>
              <TableRow>
                <TableCell>İş</TableCell>
                <TableCell>Cron</TableCell>
                <TableCell>Açıklama</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {schedules.map((s) => (
                <TableRow key={s.name}>
                  <TableCell><strong>{JOB_LABELS[s.name] || s.name}</strong></TableCell>
                  <TableCell><code>{s.expr}</code></TableCell>
                  <TableCell>{describeCron(s.expr)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <Typography variant="caption" color="text.secondary" display="block" mt={2}>
          Cron'ları değiştirmek için sunucuda <code>/opt/peaksoft-bot-backend/.env</code> düzenle ve servisi restart et:
          <br />
          <code>sudo systemctl restart peaksoft-bot</code>
        </Typography>
      </Paper>

      <Typography variant="h6" mb={1}>Manuel Bakım</Typography>
      <Paper sx={{ p: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} flexWrap="wrap">
          <TextField
            label="Kaç günden eski"
            type="number"
            size="small"
            value={retentionDays}
            onChange={(e) => setRetentionDays(e.target.value)}
            inputProps={{ min: 0, max: 3650 }}
            sx={{ width: 150 }}
            helperText="0 = hepsi"
          />
          <Button
            variant="contained"
            color="warning"
            onClick={triggerCleanup}
            disabled={cleaning}
          >
            {cleaning ? <CircularProgress size={16} color="inherit" /> : '🗑️ Şimdi Temizle'}
          </Button>
          <Typography variant="caption" color="text.secondary">
            Belirtilen günden eski run klasörleri (storage/reports altında) ve DB kayıtları silinir.
            Otomatik cron 60 günde sabit; bu alan sadece manuel tetikleme için.
          </Typography>
        </Stack>
        {cleanResult && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Temizlendi: <strong>{cleanResult.folders ?? cleanResult.runs}</strong> klasör,
            {' '}<strong>{cleanResult.files}</strong> dosya,
            ~<strong>{(cleanResult.bytes / (1024 * 1024)).toFixed(1)} MB</strong> alan kazanıldı
            {cleanResult.runs ? ` • ${cleanResult.runs} DB satırı silindi` : ''}
            {cleanResult.skipped ? ` • ${cleanResult.skipped} bilinmeyen klasör atlandı` : ''}
          </Alert>
        )}
      </Paper>
    </Box>
  );
}
